import * as React from "react";
import { ArrowRight } from "lucide-react";
import { ConnectProto, RouteStatus } from "@racoon/api";
import { getEnumName } from "@racoon/lib";
import { RouteStatusBadge } from "../RouteStatusBadge";
import { MswRouteListTable } from "../msw-route-list/MswRouteListTable";
import { RouteUnloadRow } from "../msw-route-list/RouteUnloadRow";
import { TripCascadeRow } from "../msw-route-list/trip-cascade-row";
import {
  EMPTY_SELECTED_IDS,
  RouteActionsMenu,
  useRouteActions,
} from "../msw-route-list/route-actions-menu";
import { useRouteJobsModalOutsideRouter } from "../../hooks/useRouteJobsModal";
import { getTripNumbers } from "../../utils/routeMetrics";
import {
  byId,
  nextRouteResolution,
  routeOutcome,
  routeResolutionOf,
  routeResolutionsFor,
  unfinishedRoutes,
  type JobResolution,
  type Resolutions,
  type RouteResolution,
  type RouteResolutions,
} from "~/features/force-complete-shift/lib/force-complete-shift";

type Props = {
  /** Every route of the shift: final ones keep their trip numbers but are not shown. */
  routes: ConnectProto.Route[];
  orgId: bigint;
  /** The shift's day, for the trip tools that ask for one. */
  date?: Date;
  resolutions: Resolutions;
  onResolutionChange: (jobId: bigint, resolution: JobResolution) => void;
  routeResolutions: RouteResolutions;
  onRouteResolutionChange: (routeId: bigint, resolution: RouteResolution) => void;
};

const routeStatusKey = (status: ConnectProto.RouteStatus) =>
  getEnumName(ConnectProto.RouteStatusSchema, status) as RouteStatus;

/**
 * The shift's unfinished routes as the route sheet shows them, each header saying
 * what the route becomes and each row what its jobs become. The trip menu is the
 * sheet's own, so a route can also be settled right here, ahead of the completion.
 */
export const ForceCompleteRouteCascade: React.FC<Props> = ({
  routes,
  orgId,
  date,
  resolutions,
  onResolutionChange,
  routeResolutions,
  onRouteResolutionChange,
}) => {
  const sortedRoutes = React.useMemo(() => [...routes].sort(byId), [routes]);
  // Numbered over every route of the shift, so "Рейс 2" here is "Рейс 2" on the sheet.
  const tripNumbers = React.useMemo(() => getTripNumbers(sortedRoutes), [sortedRoutes]);
  const pending = React.useMemo(() => unfinishedRoutes(routes), [routes]);
  // A modal has no router: the route card's links reload the page.
  const { openRouteJobs } = useRouteJobsModalOutsideRouter();
  const actions = useRouteActions({ routes: pending, orgId, selectedDate: date, openRouteJobs });

  const [collapsed, setCollapsed] = React.useState<Set<bigint>>(() => new Set());
  const toggle = (routeId: bigint) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });

  return (
    <div className="flex flex-col gap-2">
      {pending.map((route) => {
        const resolution = routeResolutionOf(routeResolutions, route);
        const outcome = <RouteStatusBadge status={routeStatusKey(routeOutcome(resolution))} />;
        const selectedJobIds = actions.routeSelectedMap.get(route.id) ?? EMPTY_SELECTED_IDS;
        return (
          <section
            key={String(route.id)}
            data-testid={`force-complete-shift-route-${String(route.id)}`}
          >
            <TripCascadeRow
              routeId={route.id}
              tripNumber={tripNumbers.get(route.id)}
              expanded={!collapsed.has(route.id)}
              onToggle={() => toggle(route.id)}
              onOpenRoute={() => actions.handleOpenRouteCard(route.id)}
              status={
                <>
                  <RouteStatusBadge status={routeStatusKey(route.status)} />
                  <ArrowRight className="size-4 shrink-0 text-icon-secondary" />
                  {routeResolutionsFor(route.status).length > 1 ? (
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`force-complete-shift-route-outcome-${String(route.id)}`}
                      data-resolution={resolution}
                      className="cursor-pointer rounded-full transition-opacity hover:opacity-90"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRouteResolutionChange(
                          route.id,
                          nextRouteResolution(resolution, route.status),
                        );
                      }}
                    >
                      {outcome}
                    </span>
                  ) : (
                    <span
                      data-testid={`force-complete-shift-route-outcome-${String(route.id)}`}
                      data-resolution={resolution}
                    >
                      {outcome}
                    </span>
                  )}
                </>
              }
              actions={
                <RouteActionsMenu route={route} selectedJobIds={selectedJobIds} actions={actions} />
              }
            >
              <div className="p-2">
                <MswRouteListTable
                  variant="resolution"
                  routeStatus={route.status}
                  jobs={route.jobs ?? []}
                  selectedIds={selectedJobIds}
                  onSelectionChange={(next) => actions.handleRowSelectionChange(route.id, next)}
                  resolutions={resolutions}
                  onResolutionChange={onResolutionChange}
                />
                <RouteUnloadRow route={route} orgId={orgId} disabled />
              </div>
            </TripCascadeRow>
          </section>
        );
      })}
      {actions.modals}
    </div>
  );
};
